import { useMemo } from "react";

import { colorsPalettes } from "@similarweb/styles";

import { chosenSites as chosenSitesService } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { BADGE_ITEMS } from "app/components/sidebar3/types";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { i18nFilter } from "app/filters/i18nFilter";
import { useConversionRateAvailability } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/ConversionRate/useConversionRateAvailability";
import { TrafficAndEngagementContainer } from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";
import { TrafficAndEngagementTabs } from "app/pages/website-analysis/TrafficAndEngagement/Components/TrafficAndEngagementTabs";
import { CONVERSION_RATES_TAB_NAME } from "app/pages/website-analysis/TrafficAndEngagement/ConversionRatesOverTimeTab/constants";
import {
    initPOPItemsCompareMode,
    initPOPItemsSingleMode,
    tabs,
} from "app/pages/website-analysis/TrafficAndEngagement/MD/Tabs";
import { BasicDurations } from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import {
    SELECTED_TRAFFIC_METRIC_QUERY_PARAM,
    SELECTED_ENGAGMENT_METRIC_QUERY_PARAM,
    TRAFFIC_ENGAGED_USERS_QUERY_PARAM,
    ENGAGEMENT_ENGAGED_USERS_QUERY_PARAM,
    TRAFFIC_MONTH_TO_DATE_QUERY_PARAM,
    ENGAGEMENT_MONTH_TO_DATE_QUERY_PARAM,
} from "single-spa/react-app/config/solutions25/competitionConst";

export type TrafficAndEngagementParams = {
    duration: string;
    comparedDuration: string;
    webSource: string;
    key?: string;
    isWWW?: string;
    unBounced?: string;
    trafficEngagedUsers?: string;
    engagementEngagedUsers?: string;
    [key: string]: string | undefined;
};

type TrafficAndEngagementHighLevelDataInnerProps = {
    params: TrafficAndEngagementParams;
    showGAApprovedData: boolean;
    showBetaBranchData: boolean;
};

export const TrafficAndEngagementHighLevelDataInner = ({
    params,
    showGAApprovedData,
    showBetaBranchData,
}: TrafficAndEngagementHighLevelDataInnerProps) => {
    const { hasAddonsHub } = useAddonsState();
    const chosenSites = chosenSitesService.get();
    const {
        duration,
        [SELECTED_TRAFFIC_METRIC_QUERY_PARAM]: selectedVisitsmetric,
        [SELECTED_ENGAGMENT_METRIC_QUERY_PARAM]: selectedEngagmentmetric,
        webSource,
        comparedDuration,
        key,
        isWWW,
    } = params;

    const includeSubdomains = isWWW === "*" || String(isWWW).toLowerCase() === "true";

    const { state: conversionRateAvailabilityState } = useConversionRateAvailability({
        keys: key ? key.split(",") : undefined,
        includeSubdomains,
    });

    const meta = {
        is28Days: duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
        isSingleMode: chosenSites.length === 1,
        isNonTotalWebSource: webSource !== devicesTypes.TOTAL,
        isPOP: comparedDuration !== "",
    };
    const initPOPItems = meta.isSingleMode ? initPOPItemsSingleMode : initPOPItemsCompareMode;
    const initItems = meta.isPOP ? initPOPItems : tabs;
    const hasGaToken = chosenSitesService.getPrimarySite().hasGaToken;
    const gaPrivacyStatus = chosenSitesService.getPrimarySite().privacyStatus;
    const isGa = showGAApprovedData && hasGaToken;
    const availableTabs = useMemo(
        () =>
            initItems
                .filter(
                    (item) => item.name !== CONVERSION_RATES_TAB_NAME || conversionRateAvailabilityState !== "hidden",
                )
                .map((item) => {
                    const { isAvailable, tooltip } = item;
                    const unBouncedValue =
                        params.unBounced !== undefined
                            ? params.unBounced
                            : item.type === "traffic"
                              ? params.trafficEngagedUsers
                              : params.engagementEngagedUsers;

                    const enabled = isAvailable
                        ? isAvailable({
                              isNonTotalWebSource: meta.isNonTotalWebSource,
                              unBounced: unBouncedValue,
                              duration,
                              isGa,
                              showGAApprovedData,
                              showBetaBranchData,
                          })
                        : true;
                    const tooltipText =
                        typeof tooltip === "function"
                            ? tooltip({
                                  isNonTotalWebSource: meta.isNonTotalWebSource,
                                  unBounced: unBouncedValue,
                                  duration,
                                  isGa,
                                  showGAApprovedData,
                                  showBetaBranchData,
                              })
                            : tooltip;

                    if (item.name !== CONVERSION_RATES_TAB_NAME) {
                        return { ...item, enabled, tooltip: tooltipText };
                    }

                    if (conversionRateAvailabilityState === "locked") {
                        return {
                            ...item,
                            enabled,
                            isLocked: true,
                            tooltip: tooltipText,
                            labelText: BADGE_ITEMS.NEW,
                            labelColor: colorsPalettes.orange["400"],
                        };
                    }

                    return {
                        ...item,
                        enabled,
                        labelText: BADGE_ITEMS.NEW,
                        labelColor: colorsPalettes.orange["400"],
                        tooltip: tooltipText,
                    };
                }),
        [
            initItems,
            meta.isNonTotalWebSource,
            params.unBounced,
            params.trafficEngagedUsers,
            params.engagementEngagedUsers,
            duration,
            isGa,
            showGAApprovedData,
            showBetaBranchData,
            conversionRateAvailabilityState,
        ],
    );

    const enabledTabs = useMemo(() => {
        return availableTabs.filter(({ enabled }) => enabled);
    }, [availableTabs]);

    const enabledTrafficTabs = useMemo(() => {
        return enabledTabs.filter(({ type }) => type === "traffic");
    }, [enabledTabs]);

    const enabledEngagementTabs = useMemo(() => {
        return enabledTabs.filter(({ type }) => type === "engagement");
    }, [enabledTabs]);

    const selectedVisitsTab = useMemo(() => {
        const selectedTabObject = enabledTrafficTabs.find(({ name }) => name === selectedVisitsmetric);
        return selectedTabObject ?? enabledTrafficTabs[0];
    }, [enabledTrafficTabs, selectedVisitsmetric]);

    const selectedEngagmentTab = useMemo(() => {
        const selectedTabObject = enabledEngagementTabs.find(({ name }) => name === selectedEngagmentmetric);
        return selectedTabObject ?? enabledEngagementTabs[0];
    }, [enabledEngagementTabs, selectedEngagmentmetric]);

    const i18n = i18nFilter();
    const webDedupComponent = (
        swSettings.components as Record<string, { resources?: Record<string, boolean | undefined> } | undefined>
    )["WebDedup"];
    const hasDedupeClaim = !webDedupComponent?.resources?.["IsDisabled"];
    const dedupTab = tabs.find((tab) => tab.name === "DeduplicatedAudience");
    dedupTab.isLocked = !hasDedupeClaim;
    const getGraphProps = (selectedTab: { title?: string; getTitle?: (is28d: boolean) => string } | undefined) => {
        const selectedTabTitle = selectedTab?.title ?? selectedTab?.getTitle?.(meta.is28Days) ?? "";

        return {
            meta,
            getSiteColor: chosenSitesService.getSiteColor,
            showGAApprovedData,
            showBetaBranchData,
            params,
            chosenSites,
            hasGaToken,
            name: i18n(selectedTabTitle),
            gaPrivacyStatus,
            conversionRateAvailabilityState,
        };
    };

    const graphPropsTraffic = getGraphProps(selectedVisitsTab);
    const graphPropsEngagement = getGraphProps(selectedEngagmentTab);

    const { trafficTabs, engagementTabs } = useMemo(() => {
        const grouped = availableTabs.reduce<Record<string, typeof availableTabs>>((acc, tab) => {
            (acc[tab.type] ??= []).push(tab);
            return acc;
        }, {});
        return { trafficTabs: grouped.traffic ?? [], engagementTabs: grouped.engagement ?? [] };
    }, [availableTabs]);
    return (
        <FlexColumn gap="24px">
            {selectedVisitsTab ? (
                <TrafficAndEngagementContainer>
                    <TrafficAndEngagementTabs
                        selectedTab={selectedVisitsTab}
                        availableTabs={trafficTabs}
                        params={params}
                        showGAApprovedData={showGAApprovedData}
                        showBetaBranchData={showBetaBranchData}
                        graphProps={graphPropsTraffic}
                        hasDedupeClaim={hasDedupeClaim}
                        selectedMetricQueryParam={SELECTED_TRAFFIC_METRIC_QUERY_PARAM}
                        unBouncedQueryParam={TRAFFIC_ENGAGED_USERS_QUERY_PARAM}
                        monthToDateQueryParam={TRAFFIC_MONTH_TO_DATE_QUERY_PARAM}
                        titleKey={"wa.traffic.over.time.widget.title"}
                        showExcelEngagementPopup={hasAddonsHub}
                    />
                </TrafficAndEngagementContainer>
            ) : null}
            {selectedEngagmentTab ? (
                <TrafficAndEngagementContainer>
                    <TrafficAndEngagementTabs
                        selectedTab={selectedEngagmentTab}
                        availableTabs={engagementTabs}
                        params={params}
                        showGAApprovedData={showGAApprovedData}
                        showBetaBranchData={showBetaBranchData}
                        graphProps={graphPropsEngagement}
                        hasDedupeClaim={hasDedupeClaim}
                        selectedMetricQueryParam={SELECTED_ENGAGMENT_METRIC_QUERY_PARAM}
                        unBouncedQueryParam={ENGAGEMENT_ENGAGED_USERS_QUERY_PARAM}
                        monthToDateQueryParam={ENGAGEMENT_MONTH_TO_DATE_QUERY_PARAM}
                        titleKey={"wa.engagement.over.time.widget.title"}
                        gaConnectionSupported={false}
                    />
                </TrafficAndEngagementContainer>
            ) : null}
        </FlexColumn>
    );
};
